import { useState } from "react";
import {
  useV1ProductionShopfloorDowntimeReasonCodesList,
  useV1ProductionShopfloorDowntimeReasonCodesCreate,
  useV1ProductionShopfloorDowntimeReasonCodesDestroy,
} from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";
import { useQueryClient } from "@tanstack/react-query";

export function DowntimeReasonsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useV1ProductionShopfloorDowntimeReasonCodesList();
  const reasons = data?.results || [];

  const createMutation = useV1ProductionShopfloorDowntimeReasonCodesCreate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["v1ProductionShopfloorDowntimeReasonCodesList"] });
        // Also invalidate the handcrafted query key used in DowntimeBoardPage
        qc.invalidateQueries({ queryKey: ["shopfloor", "downtime-reason-codes"] });
      },
    },
  });

  const deleteMutation = useV1ProductionShopfloorDowntimeReasonCodesDestroy({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["v1ProductionShopfloorDowntimeReasonCodesList"] });
        qc.invalidateQueries({ queryKey: ["shopfloor", "downtime-reason-codes"] });
      },
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!code.trim() || !description.trim()) {
      setError("کد و عنوان علت توقف الزامی است.");
      return;
    }
    
    try {
      await createMutation.mutateAsync({
        data: {
          code: code.trim(),
          description: description.trim(),
        },
      });
      setCode("");
      setDescription("");
      setIsModalOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "خطا در ثبت علت توقف. ممکن است کد تکراری باشد.");
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("آیا از حذف این علت توقف اطمینان دارید؟")) {
      try {
        await deleteMutation.mutateAsync({ id });
      } catch (err) {
        alert("خطا در حذف علت توقف. ممکن است در رکوردهای قبلی استفاده شده باشد.");
      }
    }
  };

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <PageBanner
        title="مدیریت علل توقف (Downtime Reasons)"
        subtitle="تعریف کدهای ثابت برای دسته‌بندی توقفات خطوط تولید"
        icon="📋"
        helpLink="/help/shopfloor/downtime-reasons"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            style={{
              padding: "0.6rem 1.2rem",
              borderRadius: "12px",
              background: "#ffffff",
              color: "#007270",
              border: "1px solid rgba(255, 255, 255, 0.8)",
              fontWeight: 700,
              fontSize: "0.9rem",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>➕</span>
            <span>تعریف علت جدید</span>
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "0", overflow: "hidden" }}>
        {isLoading ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted)", fontWeight: 600 }}>در حال بارگذاری...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
              <thead>
                <tr style={{ background: "rgba(0,0,0,0.02)", borderBottom: "1px solid var(--color-border, #E4DCC8)", color: "var(--color-text-main, #2B2B2B)" }}>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>کد</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>عنوان / شرح توقف</th>
                  <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800, width: "120px" }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {reasons.map((r) => (
                  <tr key={r.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.05)", transition: "background 0.2s" }} className="hover:bg-gray-50/50">
                    <td style={{ padding: "1rem", fontWeight: 700, color: "#555" }}>{r.code}</td>
                    <td style={{ padding: "1rem", fontWeight: 700, color: "#007270" }}>{r.description}</td>
                    <td style={{ padding: "1rem", textAlign: "center" }}>
                      <button
                        onClick={() => handleDelete(r.id)}
                        disabled={deleteMutation.isPending}
                        style={{
                          background: "rgba(239, 68, 68, 0.1)",
                          color: "#EF4444",
                          border: "none",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          cursor: deleteMutation.isPending ? "not-allowed" : "pointer",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                        }}
                      >
                        حذف
                      </button>
                    </td>
                  </tr>
                ))}
                {reasons.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted)", fontWeight: 600 }}>هیچ علت توقفی ثبت نشده است.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE MODAL */}
      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              padding: "1.5rem",
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.5rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>➕ تعریف علت توقف جدید</h3>
              <button onClick={() => setIsModalOpen(false)} style={{ background: "none", border: "none", color: "#888", fontSize: "1.2rem", cursor: "pointer" }}>
                ✕
              </button>
            </div>

            {error && (
              <div style={{ padding: "0.75rem", marginBottom: "1rem", borderRadius: "8px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid #EF4444", color: "#B91C1C", fontSize: "0.85rem", fontWeight: 600 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "6px" }}>کد توقف</label>
                <input 
                  type="text"
                  placeholder="مثال: ERR-01"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.9rem", outline: "none", direction: "ltr", textAlign: "left" }}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "6px" }}>عنوان / شرح توقف</label>
                <input 
                  type="text"
                  placeholder="مثال: خرابی موتور دستگاه"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.9rem", outline: "none" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "0.5rem", paddingTop: "1rem", borderTop: "1px solid var(--color-border, #E4DCC8)" }}>
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)} 
                  style={{ padding: "8px 16px", borderRadius: "10px", background: "none", border: "1px solid var(--color-border, #E4DCC8)", color: "#555", fontSize: "0.85rem", cursor: "pointer", fontWeight: 700 }}
                >
                  انصراف
                </button>
                <button 
                  type="submit" 
                  disabled={createMutation.isPending} 
                  style={{ padding: "8px 24px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.9rem", fontWeight: 800, cursor: createMutation.isPending ? "not-allowed" : "pointer" }}
                >
                  {createMutation.isPending ? "در حال ثبت..." : "ذخیره"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
